// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { resolveEnvFiles, type ResolvedEnv } from '../domain/envparse';
import { expansions, isEmptiedByExpansion } from '../domain/envexpand';
import { gate } from '../domain/gate';
import { maskValue } from '../domain/mask';
import { readJwt } from '../domain/jwt';
import { variantsOf } from '../domain/variants';
import type { NeedleSpec } from './engine';

/**
 * Turn the user's `.env` family into the needle set.
 *
 * The order matters and each step exists because skipping it produces a silent false clean:
 *
 *   files -> precedence resolution   (.env.production.local beats .env, and it is what shipped)
 *         -> dual-parser union       (Vite 8 uses node's parseEnv, not dotenv, and they disagree)
 *         -> expansion union         (the value in the bundle is not the value in the file)
 *         -> searchability gate      (or 76,738 match events drown the six real ones)
 *         -> escape-variant enrolment(or recall is 9/14, exactly what grep scores)
 */

export interface NeedleBuild {
  readonly specs: NeedleSpec[];
  readonly resolved: ResolvedEnv[];
  readonly skipped: { key: string; reason: string; secretShaped: boolean; value: string }[];
  readonly configBugs: { key: string; detail: string }[];
  /** How many distinct forms of each searched value were enrolled, for the clean-result copy. */
  readonly formCount: number;
}

export function buildNeedles(files: { name: string; content: string }[], mode: string): NeedleBuild {
  const resolved = resolveEnvFiles(files, mode);
  const env = new Map(resolved.map((r) => [r.key, r.value]));

  const specs: NeedleSpec[] = [];
  const skipped: NeedleBuild['skipped'] = [];
  const configBugs: NeedleBuild['configBugs'] = [];
  const seen = new Set<string>();
  let formTotal = 0;
  let searched = 0;

  for (const entry of resolved) {
    const verdict = gate(entry.key, entry.value);
    if (verdict.kind === 'skip') {
      skipped.push({
        key: entry.key,
        reason: verdict.reason,
        // The irreducible case: ADMIN_PASSWORD=admin is a real credential whose value is on the
        // deny-list. It is routed to a visible panel with a "search anyway" control rather than
        // being dropped silently or having the deny-list overridden (which would bring back
        // DEBUG_TOKEN=true and the entire noise floor).
        secretShaped: /(^|_)(KEY|SECRET|TOKEN|PASSWORD|PASS|CREDENTIAL|PRIVATE|AUTH|DSN)($|_)/i.test(entry.key),
        value: entry.value,
      });
      continue;
    }

    // Every value that any parser or any precedence level produced for this key stays enrolled.
    const candidates = [entry.value, ...entry.alternates.map((a) => a.value)];
    const expanded = expansions(entry.value, env);
    if (isEmptiedByExpansion(entry.value, expanded)) {
      configBugs.push({
        key: entry.key,
        detail:
          'This value references a variable that is not defined, so your build shipped an empty string where you expected a credential. That is a configuration bug, not a leak.',
      });
    }
    for (const e of expanded) if (!candidates.includes(e.value)) candidates.push(e.value);

    searched++;
    for (const value of candidates) {
      if (value === '') continue;
      const jwt = value.startsWith('ey') ? (readJwt(value)?.summary ?? undefined) : undefined;
      const masked = maskValue(value, jwt);
      const variants = variantsOf(value);
      formTotal += variants.length;
      for (const v of variants) {
        const dedupe = `${entry.key} ${v.text}`;
        if (seen.has(dedupe)) continue;
        seen.add(dedupe);
        specs.push({
          envKey: entry.key,
          variantId: v.id,
          text: v.text,
          masked,
          classification: verdict.classification,
          jwt,
        });
      }
    }
  }

  return {
    specs,
    resolved,
    skipped,
    configBugs,
    formCount: searched === 0 ? 0 : Math.round(formTotal / searched),
  };
}

/** Force one skipped value into the needle set — the "search anyway" control. */
export function needlesForValue(key: string, value: string): NeedleSpec[] {
  const masked = maskValue(value);
  return variantsOf(value).map((v) => ({
    envKey: key,
    variantId: v.id,
    text: v.text,
    masked,
    classification: 'search' as const,
  }));
}
